import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Save, Loader2, CheckCircle, XCircle, AlertTriangle,
  Eye, Code, ExternalLink, Copy, Check
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const TrackingPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewScripts, setPreviewScripts] = useState(null);
  
  const [config, setConfig] = useState({
    google_analytics_id: '',
    google_tag_manager_id: '',
    facebook_pixel_id: '',
    linkedin_insight_tag: '',
    hotjar_id: '',
    custom_head_scripts: '',
    custom_body_scripts: '',
    is_enabled: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/tracking-scripts');
      const data = await response.json();
      setConfig({
        google_analytics_id: data.google_analytics_id || '',
        google_tag_manager_id: data.google_tag_manager_id || '',
        facebook_pixel_id: data.facebook_pixel_id || '',
        linkedin_insight_tag: data.linkedin_insight_tag || '',
        hotjar_id: data.hotjar_id || '',
        custom_head_scripts: data.custom_head_scripts || '',
        custom_body_scripts: data.custom_body_scripts || '',
        is_enabled: data.is_enabled || false
      });
    } catch (error) {
      console.error('Failed to fetch tracking config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name, value) => {
    if (!value) return null;
    
    switch (name) {
      case 'google_analytics_id':
        if (!value.startsWith('G-') && !value.startsWith('UA-')) {
          return 'Format attendu: G-XXXXXXXXXX (GA4) ou UA-XXXXXXX-X';
        }
        break;
      case 'google_tag_manager_id':
        if (!value.startsWith('GTM-')) {
          return 'Format attendu: GTM-XXXXXXX';
        }
        break;
      case 'facebook_pixel_id':
        if (!/^\d+$/.test(value)) {
          return 'Le Pixel ID doit contenir uniquement des chiffres';
        }
        break;
      default:
        break;
    }
    return null;
  };

  const handleChange = (name, value) => {
    setConfig(prev => ({ ...prev, [name]: value }));
    
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSave = async () => {
    // Validate all fields
    const newErrors = {};
    Object.keys(config).forEach(key => {
      const error = validateField(key, config[key]);
      if (error) newErrors[key] = error;
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Veuillez corriger les erreurs avant de sauvegarder');
      return;
    }

    setSaving(true);
    try {
      await api.put('/admin/tracking-scripts', config);
      toast.success('Configuration sauvegardée avec succès');
      fetchPreview();
    } catch (error) {
      console.error('Failed to save tracking config:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await api.get('/public/tracking-scripts');
      const data = await response.json();
      setPreviewScripts(data);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchPreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Code copié dans le presse-papier');
    setTimeout(() => setCopied(false), 2000);
  };

  const ServiceCard = ({ 
    title, 
    description, 
    fieldName, 
    placeholder, 
    docUrl,
    icon: Icon,
    iconBg 
  }) => (
    <Card className="p-6 border-tp-border">
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl ${iconBg}`}>
          {Icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-tp-text">{title}</h3>
            {config[fieldName] && !errors[fieldName] && (
              <Badge className="bg-tp-success/10 text-tp-success border-tp-success text-xs">
                Configuré
              </Badge>
            )}
          </div>
          <p className="text-sm text-tp-text-light mb-4">{description}</p>
          
          <div className="space-y-2">
            <Input
              value={config[fieldName]}
              onChange={(e) => handleChange(fieldName, e.target.value)}
              placeholder={placeholder}
              className={errors[fieldName] ? 'border-tp-error' : ''}
              data-testid={`input-${fieldName}`}
            />
            {errors[fieldName] && (
              <p className="text-sm text-tp-error flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors[fieldName]}
              </p>
            )}
          </div>
          
          {docUrl && (
            <a 
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-tp-accent hover:underline mt-3"
            >
              Documentation
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="h-10 w-10 animate-spin text-tp-accent" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="tracking-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-semibold text-tp-text">Scripts de Tracking</h1>
          <p className="text-tp-text-light">Configurez vos outils d'analyse et de suivi marketing</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="tracking-enabled"
              checked={config.is_enabled}
              onCheckedChange={(checked) => handleChange('is_enabled', checked)}
              data-testid="tracking-enabled-switch"
            />
            <Label htmlFor="tracking-enabled" className="text-sm font-medium">
              {config.is_enabled ? (
                <span className="text-tp-success flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Tracking actif
                </span>
              ) : (
                <span className="text-tp-text-light flex items-center gap-1">
                  <XCircle className="h-4 w-4" />
                  Tracking désactivé
                </span>
              )}
            </Label>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-tp-accent hover:bg-tp-accent-light"
            data-testid="save-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Sauvegarder
          </Button>
        </div>
      </div>

      {!config.is_enabled && (
        <Alert className="bg-tp-warning/10 border-tp-warning">
          <AlertTriangle className="h-4 w-4 text-tp-warning" />
          <AlertDescription className="text-tp-warning">
            Le tracking est actuellement désactivé. Les scripts ne seront pas injectés sur votre site.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="services" className="space-y-6">
        <TabsList className="bg-tp-bg border border-tp-border">
          <TabsTrigger value="services" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Services
          </TabsTrigger>
          <TabsTrigger value="custom" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Scripts Personnalisés
          </TabsTrigger>
          <TabsTrigger value="preview" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Aperçu
          </TabsTrigger>
        </TabsList>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ServiceCard
              title="Google Analytics 4"
              description="Suivez les visiteurs, le comportement et les conversions sur votre site."
              fieldName="google_analytics_id"
              placeholder="G-XXXXXXXXXX"
              docUrl="https://support.google.com/analytics/answer/9539598"
              icon={
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              }
              iconBg="bg-orange-100 text-orange-600"
            />

            <ServiceCard
              title="Google Tag Manager"
              description="Gérez tous vos tags depuis une interface centralisée."
              fieldName="google_tag_manager_id"
              placeholder="GTM-XXXXXXX"
              docUrl="https://support.google.com/tagmanager/answer/6103696"
              icon={
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              }
              iconBg="bg-blue-100 text-blue-600"
            />

            <ServiceCard
              title="Facebook Pixel"
              description="Mesurez, optimisez et créez des audiences pour vos publicités Facebook."
              fieldName="facebook_pixel_id"
              placeholder="123456789012345"
              docUrl="https://www.facebook.com/business/help/952192354843755"
              icon={
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              }
              iconBg="bg-blue-600/10 text-blue-600"
            />

            <ServiceCard
              title="LinkedIn Insight Tag"
              description="Suivez les conversions et créez des audiences pour LinkedIn Ads."
              fieldName="linkedin_insight_tag"
              placeholder="123456"
              docUrl="https://www.linkedin.com/help/lms/answer/65513"
              icon={
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              }
              iconBg="bg-sky-100 text-sky-700"
            />

            <ServiceCard
              title="Hotjar"
              description="Visualisez le comportement des utilisateurs avec les heatmaps et enregistrements."
              fieldName="hotjar_id"
              placeholder="1234567"
              docUrl="https://help.hotjar.com/hc/en-us/articles/115009336727"
              icon={
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4c.552 0 1 .448 1 1v6c0 .552-.448 1-1 1s-1-.448-1-1V5c0-.552.448-1 1-1zm0 10c.552 0 1 .448 1 1v3c0 .552-.448 1-1 1s-1-.448-1-1v-3c0-.552.448-1 1-1z"/>
                </svg>
              }
              iconBg="bg-red-100 text-red-600"
            />
          </div>
        </TabsContent>

        {/* Custom Scripts Tab */}
        <TabsContent value="custom" className="space-y-6">
          <Card className="p-6 border-tp-border">
            <div className="space-y-6">
              <div>
                <Label htmlFor="custom-head" className="text-tp-text font-medium">
                  Scripts personnalisés (Head)
                </Label>
                <p className="text-sm text-tp-text-light mb-2">
                  Ces scripts seront injectés dans la balise &lt;head&gt; de votre site.
                </p>
                <Textarea
                  id="custom-head"
                  value={config.custom_head_scripts}
                  onChange={(e) => handleChange('custom_head_scripts', e.target.value)}
                  placeholder="<!-- Vos scripts personnalisés ici -->"
                  className="font-mono text-sm min-h-[150px]"
                  data-testid="custom-head-textarea"
                />
              </div>

              <div>
                <Label htmlFor="custom-body" className="text-tp-text font-medium">
                  Scripts personnalisés (Body)
                </Label>
                <p className="text-sm text-tp-text-light mb-2">
                  Ces scripts seront injectés juste après la balise &lt;body&gt;.
                </p>
                <Textarea
                  id="custom-body"
                  value={config.custom_body_scripts}
                  onChange={(e) => handleChange('custom_body_scripts', e.target.value)}
                  placeholder="<!-- Vos scripts personnalisés ici -->"
                  className="font-mono text-sm min-h-[150px]"
                  data-testid="custom-body-textarea"
                />
              </div>
            </div>
          </Card>

          <Alert className="bg-tp-primary/5 border-tp-primary">
            <Code className="h-4 w-4 text-tp-primary" />
            <AlertDescription className="text-tp-text">
              <strong>Conseil:</strong> Pour les intégrations avancées, vous pouvez utiliser Google Tag Manager 
              et gérer tous vos scripts depuis son interface. Cela permet de modifier les tags sans toucher au code.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-6">
          <Card className="p-6 border-tp-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-tp-accent" />
                <h3 className="font-semibold text-tp-text">Aperçu des Scripts Générés</h3>
              </div>
              {previewScripts?.is_enabled && (
                <Badge className="bg-tp-success/10 text-tp-success border-tp-success">
                  Actif
                </Badge>
              )}
            </div>

            {!previewScripts?.is_enabled ? (
              <Alert className="bg-tp-warning/10 border-tp-warning">
                <AlertTriangle className="h-4 w-4 text-tp-warning" />
                <AlertDescription className="text-tp-warning">
                  Le tracking est désactivé. Activez-le pour voir les scripts générés.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-6">
                {/* Configured Services */}
                <div className="flex flex-wrap gap-2">
                  {previewScripts?.config?.has_ga && (
                    <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                      Google Analytics 4
                    </Badge>
                  )}
                  {previewScripts?.config?.has_gtm && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                      Google Tag Manager
                    </Badge>
                  )}
                  {previewScripts?.config?.has_fb_pixel && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                      Facebook Pixel
                    </Badge>
                  )}
                  {previewScripts?.config?.has_linkedin && (
                    <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                      LinkedIn Insight
                    </Badge>
                  )}
                  {previewScripts?.config?.has_hotjar && (
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                      Hotjar
                    </Badge>
                  )}
                </div>

                {/* Head Scripts */}
                {previewScripts?.head_scripts && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-tp-text font-medium">Scripts &lt;head&gt;</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(previewScripts.head_scripts)}
                        className="h-8"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <pre className="bg-tp-text text-green-400 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-[300px] overflow-y-auto">
                      {previewScripts.head_scripts}
                    </pre>
                  </div>
                )}

                {/* Body Scripts */}
                {previewScripts?.body_scripts && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-tp-text font-medium">Scripts &lt;body&gt;</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(previewScripts.body_scripts)}
                        className="h-8"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <pre className="bg-tp-text text-green-400 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-[200px] overflow-y-auto">
                      {previewScripts.body_scripts}
                    </pre>
                  </div>
                )}

                {!previewScripts?.head_scripts && !previewScripts?.body_scripts && (
                  <p className="text-tp-text-light text-center py-8">
                    Aucun script configuré. Ajoutez vos IDs de tracking dans l'onglet "Services".
                  </p>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrackingPage;
